/** Presentation helpers shared by the admin console and the mobile app. */

const CA = 'en-CA';

export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString(CA, {
    style: 'currency', currency: 'CAD', minimumFractionDigits: 2,
  });
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(CA, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

export function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(CA, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export function time(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(CA, {
    hour: 'numeric', minute: '2-digit',
  });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${shortDate(iso)}, ${time(iso)}`;
}

/**
 * "3 days ago", "in 2 hours" — for news lists and draw reminders.
 *
 * Hand-rolled rather than using `Intl.RelativeTimeFormat`, which React
 * Native's Hermes engine does not implement: on a phone that throws
 * "Cannot read property 'prototype' of undefined" and takes the screen with
 * it. Everything else here uses `toLocaleString`, which Hermes does support.
 */
export function relative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';

  const diff = new Date(iso).getTime() - now;
  if (Number.isNaN(diff)) return '—';

  const future = diff >= 0;
  const abs = Math.abs(diff);

  const units: Array<[string, number]> = [
    ['year', 31536e6],
    ['month', 2592e6],
    ['week', 6048e5],
    ['day', 864e5],
    ['hour', 36e5],
    ['minute', 6e4],
  ];

  for (const [unit, ms] of units) {
    if (abs < ms) continue;
    const n = Math.round(abs / ms);

    // The near cases read better as words, which is what Intl's
    // `numeric: 'auto'` used to give us.
    if (n === 1) {
      if (unit === 'day') return future ? 'tomorrow' : 'yesterday';
      if (unit === 'week') return future ? 'next week' : 'last week';
      if (unit === 'month') return future ? 'next month' : 'last month';
      if (unit === 'year') return future ? 'next year' : 'last year';
    }

    const label = n === 1 ? unit : `${unit}s`;
    return future ? `in ${n} ${label}` : `${n} ${label} ago`;
  }

  return future ? 'in a moment' : 'just now';
}

export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

/** "Team Bolton 7 – 4 Team Scott" */
export function scoreLine(
  homeName: string | null, homeScore: number | null,
  awayName: string | null, awayScore: number | null,
): string {
  const h = homeName ?? 'TBD';
  const a = awayName ?? 'TBD';
  if (homeScore === null || awayScore === null) return `${h} vs ${a}`;
  return `${h} ${homeScore} – ${awayScore} ${a}`;
}

export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}
